import React, { PropsWithChildren, useEffect } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import TechRadarIcon from '@material-ui/icons/TrackChanges';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  SearchModalProvider,
  SidebarSearchModal,
  useSearchModal,
} from '@backstage/plugin-search';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  GroupIcon,
} from '@backstage/core-components';
import ExploreIcon from '@material-ui/icons/Explore';
import PlatformIcon from '@material-ui/icons/Layers';
// The icon every team has, in links and in the Catalog menu.
import ServiceProvidersIcon from '@material-ui/icons/People';
// The icon every service has, in links and in the Catalog menu.
import PlatformServicesIcon from '@material-ui/icons/ShoppingCart';
import SearchIcon from '@material-ui/icons/Search';
import LandscapeIcon from '@material-ui/icons/Map';
import BuildIcon from '@material-ui/icons/Build';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { MyProfileSidebarItem } from '@internal/plugin-rtl-data/src/components/menu';
import { SearchModalContent } from '../search/SearchPage';
import {
  CatalogSidebarItem,
  PinnedCatalogMenu,
  isCatalogPage,
} from './CatalogSubmenu';
import { PinnedSubmenuProvider, SidebarContent } from './PinnedSubmenu';
import { SidebarVersion } from './SidebarVersion';

const useSidebarLogoStyles = makeStyles({
  root: {
    justifyContent: 'center',
    width: 'inherit',
    height: '95px',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const useSidebarSectionStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.navigation.color,
    opacity: 0.7,
    padding: theme.spacing(1, 3, 0),
  },
}));

/**
 * Names a section of the sidebar. The label of a `SidebarGroup` only shows on
 * a phone, where the group is a button of the bottom bar; on a desktop this is
 * what tells the sections apart. A collapsed sidebar has no room for it and
 * keeps just the dividers.
 */
const SidebarSection = (props: { title: string }) => {
  const classes = useSidebarSectionStyles();
  const { isOpen } = useSidebarOpenState();

  if (!isOpen) {
    return null;
  }
  return (
    <Typography variant="overline" component="div" className={classes.root}>
      {props.title}
    </Typography>
  );
};

const useSidebarShortcutsStyles = makeStyles({
  root: {
    width: '100%',
  },
});

/**
 * The shortcuts, scrolling with the rest of the sidebar. The plugin puts them
 * into a `SidebarScrollWrapper` of their own, which may shrink and then
 * scrolls by itself - inside the sidebar, which scrolls as a whole already.
 * The sidebar does not shrink what it holds, so in a plain block of its own
 * the wrapper is as tall as the shortcuts and has nothing left to scroll.
 */
const SidebarShortcuts = () => {
  const classes = useSidebarShortcutsStyles();
  return (
    <div className={classes.root}>
      <Shortcuts />
    </div>
  );
};

/**
 * Cmd+E on a Mac, Ctrl+E everywhere else, opens the search popup from any
 * page and closes it again. Ctrl+E is left alone on a Mac, where it moves the
 * cursor to the end of the line in a text field.
 *
 * It drives the same modal the sidebar item opens: `SidebarSearchModal` takes
 * over the state of a `SearchModalProvider` above it instead of keeping its
 * own, and this sits under that provider too.
 */
const SearchShortcut = () => {
  const { state, setOpen } = useSearchModal();
  const hidden = state.hidden;

  useEffect(() => {
    const isMac = /mac/i.test(navigator.platform);
    const onKeyDown = (event: KeyboardEvent) => {
      const modifier = isMac ? event.metaKey : event.ctrlKey;
      if (
        !modifier ||
        event.altKey ||
        event.shiftKey ||
        event.key.toLowerCase() !== 'e'
      ) {
        return;
      }
      // The browser has its own use for the combination - the address bar,
      // or the find bar on a Mac.
      event.preventDefault();
      if (!event.repeat) {
        setOpen(hidden);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hidden, setOpen]);

  return null;
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SearchModalProvider>
    <SearchShortcut />
    <SidebarPage>
      <PinnedSubmenuProvider isMenuPage={isCatalogPage}>
        <Sidebar>
          <SidebarLogo />
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <MyProfileSidebarItem />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            {/* The popup shows the same sections as the search page, so a hit
            looks and sits the same in both. Its state is the provider's
            around the page, which `SearchShortcut` opens it through. */}
            <SidebarSearchModal>
              {({ toggleModal }) => (
                <SearchModalContent toggleModal={toggleModal} />
              )}
            </SidebarSearchModal>
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Discover" icon={<ExploreIcon />}>
            <SidebarSection title="Discover" />
            <CatalogSidebarItem />
            <SidebarItem icon={LandscapeIcon} to="landscape" text="Landscape" />
            <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
            <SidebarItem
              icon={TechRadarIcon}
              to="tech-radar"
              text="Tech Radar"
            />
            <SidebarItem
              icon={GroupIcon}
              to="organization"
              text="Organization"
            />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Platform" icon={<PlatformIcon />}>
            <SidebarSection title="Platform" />
            <SidebarItem
              icon={CreateComponentIcon}
              to="create"
              text="Scaffolder"
            />
            <FeatureFlagged with="platform-catalog">
              <SidebarItem
                icon={ServiceProvidersIcon}
                to="service-providers"
                // Short, as the entry next to it: under the Platform heading
                // it reads as the service providers, and the full name does
                // not fit the sidebar.
                text="Providers"
              />
              <SidebarItem
                icon={PlatformServicesIcon}
                to="platform-services"
                text="Services"
              />
            </FeatureFlagged>
            <SidebarItem
              icon={MenuBookIcon}
              to="https://rtltech.atlassian.net/wiki/spaces/DP/pages/1863992495/Homepage"
              text="Platform Docs"
              target="_blank"
              rel="noopener noreferrer"
            />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarShortcuts />
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
            <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
          </SidebarGroup>
          <SidebarVersion />
        </Sidebar>
        <PinnedCatalogMenu />
        <SidebarContent>{children}</SidebarContent>
      </PinnedSubmenuProvider>
    </SidebarPage>
  </SearchModalProvider>
);
